/* jgd-ad-tracking — stores campaign referrer parameters and landed-on
   products as cart attributes via /cart/update.js. Data waits in
   localStorage (max. REF_TTL_MS) and is flushed on page load and on the
   theme's `cart:update` event (ThemeEvents.cartUpdate) once the cart has
   items; a TTL-bounded sync marker avoids redundant requests.

   v2 (2026-09-30):
   - Meta click ID: a landing with ?fbclid= is stored as `reffbc` = fb.1.<ms>.<fbclid> (Meta format) and written as cart
     attribute reffbc; the `_fbc` cookie (90 days) is set only after marketing consent, so the pixel also gets the click
     ID when the visitor consents after navigating on. Same 21-day lifetime as the Google IDs.
   - Attributes travel with the checkout form: on submit of the cart form (action /cart, not /cart/add|change|update)
     the known ref values are added as hidden fields attributes[...] in the same request that starts the checkout.
     Never empty fields (an empty field deletes the attribute). Wallet/express buttons bypass the form; the async
     write stays as the safety net.
   - The sync marker is bound to the current cart (Shopify cookie `cart`); another cart makes it invalid.
   - Cart from the theme event: Horizon's CartUpdateEvent (cart page) carries the full cart in detail.resource and saves
     the GET /cart.js; CartAddEvent (product form) carries none, then /cart.js is read as before. The flush runs when the
     browser is idle (requestIdleCallback, max. 2 s), after the theme has rendered and apart from other cart writers.
   - refcons: Shopify Customer Privacy API first (decided yes/no), then localStorage refConsent, else deny. Values are
     always allow/deny (the order-attribute filter of the offline upload expects them); the former fallback 'grant' is
     gone.
   No shared cart writer / fetch wrapper (incident 2026-09-27). */
(function () {
  var PENDING_KEY = 'jgd-ad-products-pending';
  var MARKER_KEY = 'jgd-ad-tracking-synced';
  var CAPTURED_AT_KEY = 'jgd-ad-captured-at';
  var MAX_ENTRIES = 15;
  var REF_TTL_MS = 21 * 24 * 60 * 60 * 1000;
  var MARKER_TTL_MS = 60 * 60 * 1000;
  var flushing = false;
  var queued = false;
  var queuedForce = false;

  var REF_ATTRIBUTES = {
    refid: 'ref',
    refgid: 'refgid',
    refgbid: 'refgbid',
    refwbid: 'refwbid',
    reffbc: 'reffbc',
    refcons: 'refConsent'
  };
  var REF_VALUE_KEYS = ['ref', 'refgid', 'refgbid', 'refwbid', 'reffbc'];

  function lsGet(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  function lsSet(key, value) {
    try { localStorage.setItem(key, value); } catch (e) {}
  }

  function lsRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
  }

  function readPending() {
    try { return JSON.parse(lsGet(PENDING_KEY)) || []; } catch (e) { return []; }
  }

  function writePending(list) {
    if (list.length) {
      lsSet(PENDING_KEY, JSON.stringify(list));
    } else {
      lsRemove(PENDING_KEY);
    }
  }

  function privacyConsent() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.currentVisitorConsent === 'function') return cp.currentVisitorConsent();
    } catch (e) {}
    return null;
  }

  // v2: decided Shopify consent first, then the stored value, else deny; always allow/deny.
  function consentValue() {
    var c = privacyConsent();
    if (c && c.marketing === 'yes') return 'allow';
    if (c && c.marketing === 'no') return 'deny';
    var v = lsGet(REF_ATTRIBUTES.refcons);
    if (v === 'allow' || v === 'grant') return 'allow';
    return 'deny';
  }

  function refValues() {
    var values = {};
    var hasRef = false;
    Object.keys(REF_ATTRIBUTES).forEach(function (attrKey) {
      if (attrKey === 'refcons') return;
      var value = lsGet(REF_ATTRIBUTES[attrKey]);
      if (value) {
        values[attrKey] = value;
        hasRef = true;
      }
    });
    if (hasRef) values.refcons = consentValue();
    return values;
  }

  function hasData() {
    return readPending().length > 0 || Object.keys(refValues()).length > 0;
  }

  function expireStale() {
    if (!hasData()) return;
    var ts = parseInt(lsGet(CAPTURED_AT_KEY), 10);
    if (!ts) {
      lsSet(CAPTURED_AT_KEY, String(Date.now()));
      return;
    }
    if (Date.now() - ts > REF_TTL_MS) {
      REF_VALUE_KEYS.forEach(function (key) { lsRemove(key); });
      writePending([]);
      lsRemove(CAPTURED_AT_KEY);
      lsRemove(MARKER_KEY);
    }
  }

  function currentSig() {
    return JSON.stringify([refValues(), readPending()]);
  }

  // v2: Meta click ID. Format fb.<version>.<subdomainIndex>.<creationTime ms>.<fbclid>; the fbclid is everything after
  // the third dot (it may contain dots itself).
  function fbcParts(value) {
    var t = String(value || '').split('.');
    if (t.length < 4 || t[0] !== 'fb') return null;
    return { time: parseInt(t[2], 10) || 0, fbclid: t.slice(3).join('.') };
  }
  function readCookie(name) {
    var parts = ('; ' + document.cookie).split('; ' + name + '=');
    return parts.length === 2 ? decodeURIComponent(parts.pop().split(';').shift()) : '';
  }
  function captureFbclid(fbclid) {
    var old = fbcParts(lsGet('reffbc'));
    if (old && old.fbclid === fbclid) return;
    var cookie = fbcParts(readCookie('_fbc'));
    var value = cookie && cookie.fbclid === fbclid ? readCookie('_fbc') : 'fb.1.' + Date.now() + '.' + fbclid;
    lsSet('reffbc', value);
    lsSet(CAPTURED_AT_KEY, String(Date.now()));
  }
  function marketingAllowed() {
    var c = privacyConsent();
    return !!(c && c.marketing === 'yes');
  }
  // _fbc only after marketing consent; never overwrite a newer existing value.
  function setFbcCookie() {
    var value = lsGet('reffbc'); var fresh = fbcParts(value);
    if (!fresh || !marketingAllowed()) return;
    var existing = fbcParts(readCookie('_fbc'));
    if (existing && (existing.fbclid === fresh.fbclid || existing.time >= fresh.time)) return;
    var host = location.hostname.split('.'); var domain = host.length > 2 ? host.slice(-2).join('.') : location.hostname;
    var expires = new Date(Date.now() + 90 * 864e5).toUTCString();
    try { document.cookie = '_fbc=' + value + '; expires=' + expires + '; path=/; domain=.' + domain + '; SameSite=Lax' + (location.protocol === 'https:' ? '; Secure' : ''); } catch (e) {}
  }

  function captureAdClick(params) {
    var gclid = params.get('gclid');
    var gbraid = params.get('gbraid');
    var wbraid = params.get('wbraid');
    lsSet('ref', location.href);
    if (gclid) lsSet('refgid', gclid);
    if (gbraid) lsSet('refgbid', gbraid);
    if (wbraid) lsSet('refwbid', wbraid);
    lsSet(CAPTURED_AT_KEY, String(Date.now()));
  }

  function adEntry(params) {
    var pid = null;
    var vid = params.get('variant');
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && m.product.id) pid = String(m.product.id);
      if (!vid && m) {
        if (m.selectedVariantId) {
          vid = String(m.selectedVariantId);
        } else if (m.product && m.product.variants && m.product.variants.length) {
          vid = String(m.product.variants[0].id);
        }
      }
    } catch (e) {}
    if (pid) return pid + (vid ? ':' + vid : '');
    var match = location.pathname.match(/\/products\/([^/]+)/);
    return match ? 'h:' + match[1] : null;
  }

  function readMarker() {
    try { return JSON.parse(lsGet(MARKER_KEY)); } catch (e) { return null; }
  }

  // v2: Shopify cookie 'cart' = the current cart (readable by JS).
  function cartCookie() {
    var parts = ('; ' + document.cookie).split('; cart=');
    return parts.length === 2 ? parts.pop().split(';').shift() : '';
  }

  // v2: hidden fields attributes[...] at the moment the checkout form is submitted. Only known values, never empty.
  function setField(form, name, value) {
    var inp = form.querySelector('input[name="' + name + '"]');
    if (!inp) { inp = document.createElement('input'); inp.type = 'hidden'; inp.name = name; form.appendChild(inp); }
    inp.value = value;
  }
  function isCheckoutForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var a = form.getAttribute('action') || '';
    return /\/cart\/?(\?|$)/.test(a) && !/\/cart\/(add|change|update)/.test(a);
  }
  document.addEventListener('submit', function (ev) {
    try {
      if (!isCheckoutForm(ev.target)) return;
      var values = refValues();
      Object.keys(values).forEach(function (k) { if (values[k]) setField(ev.target, 'attributes[' + k + ']', values[k]); });
    } catch (e) {}
  }, true);

  function flush(force, cart) {
    if (!hasData()) return;
    if (flushing) {
      queued = true;
      queuedForce = queuedForce || force;
      return;
    }
    if (!force) {
      var marker = readMarker();
      if (marker && marker.sig === currentSig() && marker.cc === cartCookie() &&
          marker.ts && Date.now() - marker.ts < MARKER_TTL_MS) return;
    }
    flushing = true;
    (cart && typeof cart.item_count === 'number' && cart.attributes
      ? Promise.resolve(cart)
      : fetch('/cart.js').then(function (r) { return r.json(); }))
      .then(function (cart) {
        var attrs = cart.attributes || {};
        var ourKeys = Object.keys(REF_ATTRIBUTES).concat('ad_products');
        var cartHasOurs = ourKeys.some(function (k) { return attrs[k]; });
        if (!cart.item_count && !cartHasOurs) return;

        var payload = {};
        var desired = refValues();
        Object.keys(desired).forEach(function (key) {
          if (String(attrs[key] || '') !== desired[key]) payload[key] = desired[key];
        });

        var stored = String(attrs.ad_products || '').split(',').filter(Boolean);
        var pendingAtRead = readPending();
        var list = stored.slice();
        pendingAtRead.forEach(function (entry) {
          if (list.indexOf(entry) === -1) list.push(entry);
        });
        if (list.length !== stored.length) {
          payload.ad_products = list.slice(-MAX_ENTRIES).join(',');
        }

        var syncedSig = JSON.stringify([desired, []]);

        function markSynced() {
          var remaining = readPending().filter(function (entry) {
            return pendingAtRead.indexOf(entry) === -1;
          });
          writePending(remaining);
          if (remaining.length) {
            lsRemove(MARKER_KEY);
            queued = true;
          } else {
            lsSet(MARKER_KEY, JSON.stringify({
              sig: syncedSig,
              token: cart.token,
              cc: cartCookie(),
              ts: Date.now()
            }));
          }
        }

        if (!Object.keys(payload).length) {
          markSynced();
          return;
        }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: payload })
        }).then(function (r) {
          if (r.ok) markSynced();
        });
      })
      .catch(function () {})
      .then(function () {
        flushing = false;
        if (queued) {
          queued = false;
          var f = queuedForce;
          queuedForce = false;
          flush(f);
        }
      });
  }

  // v2: run when idle (after the theme has rendered); max. 2 s.
  function whenIdle(fn) {
    if (window.requestIdleCallback) { window.requestIdleCallback(fn, { timeout: 2000 }); }
    else { setTimeout(fn, 800); }
  }

  function run() {
    expireStale();
    var params = new URLSearchParams(location.search);
    var isAd = params.has('gclid') || params.has('gbraid') || params.has('wbraid') ||
      (params.get('utm_source') === 'google' && params.get('utm_medium') === 'cpc');
    if (isAd) {
      captureAdClick(params);
      var entry = adEntry(params);
      if (entry) {
        var pending = readPending();
        if (pending.indexOf(entry) === -1) {
          pending.push(entry);
          writePending(pending.slice(-MAX_ENTRIES));
        }
      }
    }
    // v2: Meta click ID independent of Google parameters; cookie now (consent already given) or on the decision.
    var fbclid = params.get('fbclid');
    if (fbclid) captureFbclid(fbclid);
    setFbcCookie();
    if (hasData()) flush(false);
    document.addEventListener('cart:update', function (ev) {
      var r = ev && ev.detail && ev.detail.resource;
      var cart = r && Array.isArray(r.items) ? r : null;
      whenIdle(function () { flush(true, cart); });
    });
    document.addEventListener('visitorConsentCollected', function () { setFbcCookie(); flush(true); });
    window.addEventListener('pageshow', function (ev) {
      if (ev.persisted) {
        flushing = false;
        flush(false);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
